import { useState, useEffect } from "react";
import { useParams, Link as RouterLink } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import {
  Box,
  Button,
  Container,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
  TextField,
  IconButton,
  Tooltip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { Loading } from "../../components/Loading";
import { ErrorPageDetails } from "../../components/ErrorPageDetails";
import DetailsLabel from "../../components/DetailsLabel";
import StatusBadge from "../../components/StatusBadge";
import { DATE_TIME_FORMAT } from "../../utils/constants";
import { copyToClipboard } from "../../utils/helpers";
import {
  getPayoutRequestById,
  payPayoutRequest,
  rejectPayoutRequest,
  PayoutRequestStatus,
} from "../../services/payoutRequest.service";

const InnerContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  flexWrap: "wrap",
});

const RowContainer = styled(Box)({
  display: "flex",
  flexDirection: "row",
  flexWrap: "wrap",
  marginBlock: "16px",
  padding: 0,
});

const ActionContainer = styled(Box)({
  display: "flex",
  gap: "16px",
  marginTop: "24px",
});

const PayoutRequestView = () => {
  const { id } = useParams();
  const [openPayDialog, setOpenPayDialog] = useState(false);
  const [openRejectDialog, setOpenRejectDialog] = useState(false);
  const [payTxid, setPayTxid] = useState("");
  const [payTxidError, setPayTxidError] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [rejectReasonError, setRejectReasonError] = useState("");

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["payoutRequest", id],
    queryFn: () => getPayoutRequestById(id),
  });

  const {
    mutate: pay,
    isPending: isPaying,
    isSuccess: isPaySuccess,
    isError: isPayError,
    error: payError,
  } = useMutation({
    mutationKey: ["payPayoutRequest", id],
    mutationFn: (txid) => payPayoutRequest(id, txid),
  });

  const {
    mutate: reject,
    isPending: isRejecting,
    isSuccess: isRejectSuccess,
    isError: isRejectError,
    error: rejectError,
  } = useMutation({
    mutationKey: ["rejectPayoutRequest", id],
    mutationFn: (reason) => rejectPayoutRequest(id, reason),
  });

  useEffect(() => {
    if (isPaySuccess) {
      alert("Payout request approved successfully");
      setOpenPayDialog(false);
      refetch();
    }
  }, [isPaySuccess, refetch]);

  useEffect(() => {
    if (isRejectSuccess) {
      alert("Payout request rejected successfully");
      setOpenRejectDialog(false);
      refetch();
    }
  }, [isRejectSuccess, refetch]);

  useEffect(() => {
    if (isPayError) {
      const message =
        payError?.message || payError || "Error processing payout request";
      alert(message);
    }
  }, [isPayError, payError]);

  useEffect(() => {
    if (isRejectError) {
      const message =
        rejectError?.message || rejectError || "Error rejecting payout request";
      alert(message);
    }
  }, [isRejectError, rejectError]);

  const handlePayClick = () => {
    setPayTxid("");
    setPayTxidError("");
    setOpenPayDialog(true);
  };

  const handleRejectClick = () => {
    setRejectReason("");
    setRejectReasonError("");
    setOpenRejectDialog(true);
  };

  const handlePayConfirm = () => {
    const trimmedTxid = payTxid.trim();
    if (!trimmedTxid) {
      setPayTxidError("Transaction ID (txid) is required");
      return;
    }

    pay(trimmedTxid);
  };

  const handleRejectConfirm = () => {
    const trimmedReason = rejectReason.trim();
    if (trimmedReason.length > 255) {
      setRejectReasonError("Reason must be 255 characters or less");
      return;
    }

    reject(trimmedReason || undefined);
  };

  if (isLoading) {
    return <Loading />;
  }

  if (isError || !data) {
    return <ErrorPageDetails text="Error loading payout request" />;
  }

  const isPending = data.status === PayoutRequestStatus.Pending;

  return (
    <Container component="main">
      <InnerContainer>
        <Typography component="h1" variant="h4" gutterBottom>
          Payout Request Details
        </Typography>

        <RowContainer>
          <Box sx={{ flex: 1, minWidth: "300px" }}>
            <Typography component="h2" variant="h5" gutterBottom>
              Request Information
            </Typography>

            <DetailsLabel label="Request ID" value={data.id} />

            <DetailsLabel
              label="Credits Requested"
              value={data.creditsRequested}
            />

            <DetailsLabel
              label="Wallet Address"
              value={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Tooltip title={data.walletAddress}>
                    <span>{data.walletAddress}</span>
                  </Tooltip>

                  <IconButton
                    size="small"
                    onClick={(e) => copyToClipboard(data.walletAddress, e)}
                    sx={{ padding: 0.5 }}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Box>
              }
            />

            <DetailsLabel
              label="Payment Method"
              value={
                <span style={{ textTransform: "capitalize" }}>
                  {data.method}
                </span>
              }
            />

            <DetailsLabel
              label="Status"
              value={<StatusBadge status={data.status} />}
            />

            <DetailsLabel
              label="Created At"
              value={format(new Date(data.createdAt), DATE_TIME_FORMAT)}
            />

            <DetailsLabel
              label="Updated At"
              value={format(new Date(data.updatedAt), DATE_TIME_FORMAT)}
            />
          </Box>

          <Box sx={{ flex: 1, minWidth: "300px", ml: { xs: 0, md: 4 } }}>
            <Typography component="h2" variant="h5" gutterBottom>
              User Information
            </Typography>

            {data.user ? (
              <>
                <DetailsLabel
                  label="User ID"
                  value={
                    <Link
                      component={RouterLink}
                      to={`/users/${data.user.id}`}
                      underline="hover"
                    >
                      {data.user.id}
                    </Link>
                  }
                />

                <DetailsLabel label="Email" value={data.user.email} />

                <DetailsLabel
                  label="Username"
                  value={data.user.username ? `@${data.user.username}` : "N/A"}
                />
              </>
            ) : (
              <DetailsLabel label="User" value="N/A" />
            )}
          </Box>
        </RowContainer>

        {isPending && (
          <ActionContainer>
            <Button
              variant="contained"
              color="success"
              onClick={handlePayClick}
              disabled={isPaying || isRejecting}
            >
              Approve
            </Button>

            <Button
              variant="outlined"
              color="error"
              onClick={handleRejectClick}
              disabled={isPaying || isRejecting}
            >
              Reject
            </Button>
          </ActionContainer>
        )}
      </InnerContainer>

      <Dialog
        open={openPayDialog}
        onClose={() => !isPaying && setOpenPayDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Approve Payout</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to approve this payout request? This will
            deduct {data.creditsRequested} credits from the user&apos;s wallet
            and create a payout transaction.
          </Typography>

          <Box
            sx={{
              mt: 2,
              p: 2,
              backgroundColor: "#f5f5f5",
              borderRadius: 1,
            }}
          >
            <Typography variant="subtitle2" color="text.secondary">
              Summary:
            </Typography>

            <Typography variant="body2">
              User: {data.user?.email || "N/A"}
            </Typography>

            <Typography variant="body2">
              Credits: {data.creditsRequested}
            </Typography>

            <Typography variant="body2">Method: {data.method}</Typography>
          </Box>

          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            Please ensure you have completed the external payment transfer
            before confirming.
          </Typography>

          <TextField
            fullWidth
            required
            label="Transaction ID (txid)"
            value={payTxid}
            onChange={(event) => {
              setPayTxid(event.target.value);
              if (payTxidError) {
                setPayTxidError("");
              }
            }}
            error={Boolean(payTxidError)}
            helperText={payTxidError || "Enter the payout transaction ID"}
            sx={{ mt: 2 }}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenPayDialog(false)} disabled={isPaying}>
            Cancel
          </Button>

          <Button
            onClick={handlePayConfirm}
            variant="contained"
            color="success"
            disabled={isPaying}
          >
            {isPaying ? "Processing..." : "Approve"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openRejectDialog}
        onClose={() => !isRejecting && setOpenRejectDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reject Payout Request</DialogTitle>

        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to reject this payout request? This will
            unblock the credits and return them to the user&apos;s available
            balance.
          </Typography>

          <TextField
            fullWidth
            label="Reason (optional)"
            value={rejectReason}
            onChange={(event) => {
              setRejectReason(event.target.value);
              if (rejectReasonError) {
                setRejectReasonError("");
              }
            }}
            error={Boolean(rejectReasonError)}
            helperText={rejectReasonError || "Max 255 characters"}
            inputProps={{ maxLength: 255 }}
            sx={{ mt: 2 }}
            multiline
            minRows={3}
          />
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setOpenRejectDialog(false)}
            disabled={isRejecting}
          >
            Cancel
          </Button>

          <Button
            onClick={handleRejectConfirm}
            variant="contained"
            color="error"
            disabled={isRejecting}
          >
            {isRejecting ? "Rejecting..." : "Reject"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PayoutRequestView;
